import { parseSelectFilterOptions } from '../../../util/search';
import { normalizeSellerParam } from '../sellerSearchParams';

export { normalizeSellerParam };

export const SPONSORED_PARAM = 'pub_authorUserType';
export const RATED_PARAM = 'pub_sellerHasReviews';
export const SELLER_PARAM = 'seller';

export const RATED_FIELD = 'ratedSellers';

export const SPONSORED_VALUE = 'commercial_sponsor';
export const RATED_VALUE = 'true';

export const SELLER_TYPE_FIELD = 'sellerType';
export const SELLER_TYPE_VENDOR = 'vendor';
export const SELLER_TYPE_HOBBYIST = 'hobbyist';
export const SELLER_TYPE_SPONSORED = 'sponsored';

export const VENDOR_USER_TYPES = ['commercial_non_sponsor', 'commercial_sponsor'];
export const HOBBYIST_USER_TYPES = [
  'non_supporting_hobbyist_buy_sell',
  'supporting_hobbyist_buy_sell',
];

export const VENDOR_PARAM_VALUE = `has_any:${VENDOR_USER_TYPES.join(',')}`;
export const HOBBYIST_PARAM_VALUE = `has_any:${HOBBYIST_USER_TYPES.join(',')}`;

const setIsSubset = (values, allowed) =>
  values.size > 0 && [...values].every(value => allowed.has(value));

export const isParamActive = value => value === true || value === 'true';

export const isSellerFilterActive = value => normalizeSellerParam(value) !== '';

export const isRatedSellersFilterActive = urlQueryParams =>
  isParamActive(urlQueryParams?.[RATED_PARAM]);

export const sellerDisplayName = seller => {
  const username = typeof seller?.username === 'string' ? seller.username.trim() : '';
  if (username) {
    return username;
  }
  const displayName = typeof seller?.displayName === 'string' ? seller.displayName.trim() : '';
  return displayName;
};

const AVATAR_TINTS = [
  'var(--r2rAccent)',
  'var(--r2rOrange)',
  'var(--colorGrey700)',
  'var(--colorSuccessDark)',
  'var(--colorFailDark)',
  'var(--colorGrey800)',
  'var(--marketplaceColor)',
  'var(--colorGrey600)',
];

export const avatarTintForId = id => {
  const s = String(id || '');
  let hash = 0;
  for (let i = 0; i < s.length; i += 1) {
    hash = (hash * 31 + s.charCodeAt(i)) | 0;
  }
  return AVATAR_TINTS[Math.abs(hash) % AVATAR_TINTS.length];
};

export const avatarInitial = name => {
  const match = String(name || '').match(/[a-zA-Z0-9]/);
  return match ? match[0].toUpperCase() : '?';
};

/**
 * Split `text` so the first case-insensitive `query` hit can be bolded.
 * @returns {Array<{ text: string, hit: boolean }>}
 */
export const splitMatchedText = (text, query) => {
  const hay = typeof text === 'string' ? text : '';
  const needle = typeof query === 'string' ? query.trim() : '';
  if (!hay) {
    return [];
  }
  if (needle.length < 2) {
    return [{ text: hay, hit: false }];
  }
  const index = hay.toLowerCase().indexOf(needle.toLowerCase());
  if (index < 0) {
    return [{ text: hay, hit: false }];
  }
  return [
    { text: hay.slice(0, index), hit: false },
    { text: hay.slice(index, index + needle.length), hit: true },
    { text: hay.slice(index + needle.length), hit: false },
  ].filter(part => part.text);
};

/**
 * Whether a listing's seller has public reviews (matches ListingCard rating row logic).
 * Prefers bridge-stamped publicData.sellerHasReviews when present; otherwise falls
 * back to author profile metadata already included in search results.
 */
export const listingHasRatedSeller = listing => {
  const publicFlag = listing?.attributes?.publicData?.sellerHasReviews;
  if (publicFlag === true) {
    return true;
  }
  if (publicFlag === false) {
    return false;
  }

  const reviewCountRaw = listing?.author?.attributes?.profile?.metadata?.reviewCount;
  const reviewAverageRaw = listing?.author?.attributes?.profile?.metadata?.reviewAverage;
  const ratingCount = Number.isFinite(reviewCountRaw) ? reviewCountRaw : 0;
  const ratingAverage = Number.isFinite(reviewAverageRaw) ? reviewAverageRaw : null;
  return ratingCount > 0 && ratingAverage != null;
};

export const filterListingsForRatedSellers = (listings, urlQueryParams) => {
  if (!isRatedSellersFilterActive(urlQueryParams)) {
    return listings;
  }
  // Safety net when server-side pub_sellerHasReviews filtering is stale or missing stamps.
  return listings.filter(listingHasRatedSeller);
};

/**
 * Map a raw pub_authorUserType URL value to the Sellers filter seller-type field.
 * Mixed or unknown sets return '' so no checkbox is invented.
 */
export const classifySellerType = rawValue => {
  if (rawValue == null || rawValue === '') {
    return '';
  }
  const parsed = parseSelectFilterOptions(String(rawValue)).filter(Boolean);
  const values = new Set(parsed);
  if (values.size === 0) {
    return '';
  }
  if (values.size === 1 && values.has(SPONSORED_VALUE)) {
    return SELLER_TYPE_SPONSORED;
  }
  if (setIsSubset(values, new Set(VENDOR_USER_TYPES))) {
    return SELLER_TYPE_VENDOR;
  }
  if (setIsSubset(values, new Set(HOBBYIST_USER_TYPES))) {
    return SELLER_TYPE_HOBBYIST;
  }
  return '';
};

export const sellerTypeToParamValue = sellerType => {
  if (sellerType === SELLER_TYPE_VENDOR) {
    return VENDOR_PARAM_VALUE;
  }
  if (sellerType === SELLER_TYPE_HOBBYIST) {
    return HOBBYIST_PARAM_VALUE;
  }
  if (sellerType === SELLER_TYPE_SPONSORED) {
    return SPONSORED_VALUE;
  }
  return null;
};

export const resolveSellerTypeFromForm = (formSellerType, previousSellerType = '') => {
  const selected = Array.isArray(formSellerType)
    ? formSellerType.filter(Boolean)
    : formSellerType
      ? [formSellerType]
      : [];
  if (selected.length === 0) {
    return '';
  }
  if (selected.length === 1) {
    return selected[0];
  }
  // Final Form checkbox groups append the newly checked value. Prefer that over
  // the first non-previous value so a late click wins before the URL re-renders.
  const last = selected[selected.length - 1];
  if (last !== previousSellerType) {
    return last;
  }
  return selected.find(value => value !== previousSellerType) || last;
};

export const toUrlParams = (formValues, { previousSellerType } = {}) => {
  const sellerType = resolveSellerTypeFromForm(
    formValues?.[SELLER_TYPE_FIELD],
    previousSellerType
  );
  return {
    [SPONSORED_PARAM]: sellerTypeToParamValue(sellerType),
    [RATED_PARAM]: formValues?.[RATED_FIELD]?.includes?.(RATED_VALUE) ? 'true' : null,
  };
};

export const clearUrlParams = () => ({
  [SPONSORED_PARAM]: null,
  [RATED_PARAM]: null,
  [SELLER_PARAM]: null,
});
